import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Chip, Typography, Box,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Grid, Stepper, Step, StepLabel,
  CircularProgress, Divider, Alert,
} from '@mui/material';
import { Visibility, SportsScore, Edit, Delete, Warning } from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';
import { BASE_URL, apiCall } from '../api/apiClient';
import GameViewDialog from './GameViewDialog';

// ─── Shared Design Tokens ─────────────────────────────────────────────────────
const BG       = '#f4f7f5';
const SURFACE  = '#ffffff';
const BORDER   = '#d6e4de';
const ACCENT   = '#1e6b45';
const ACCENT_BG = '#e6f2ec';
const TEXT_PRI = '#0f1f14';
const TEXT_SEC = '#4b6358';

// ─── Shared input style ───────────────────────────────────────────────────────
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    color: TEXT_PRI,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: ACCENT },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
  },
  '& .MuiInputLabel-root': { color: TEXT_SEC },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
};

// ─── Status & category maps ───────────────────────────────────────────────────
const statusColor = {
  live: 'error',
  scheduled: 'info',
  completed: 'success',
  postponed: 'warning',
  cancelled: 'default',
};

const CATEGORIES = ['group_stage', 'round_of_16', 'quarter_final', 'semi_final', 'final', 'league', 'friendly'];
const CATEGORY_LABELS = {
  group_stage:  'Phase de groupes',
  round_of_16:  'Huitièmes de finale',
  quarter_final:'Quarts de finale',
  semi_final:   'Demi-finales',
  final:        'Finale',
  league:       'Championnat',
  friendly:     'Match amical',
};
const STATUSES = ['scheduled', 'live', 'completed', 'postponed', 'cancelled'];
const STATUS_LABELS = {
  scheduled: 'Planifié',
  live:      'En cours',
  completed: 'Terminé',
  postponed: 'Reporté',
  cancelled: 'Annulé',
};

// ─── Edit Dialog ──────────────────────────────────────────────────────────────
const GameEditDialog = ({ open, onClose, game, onRefresh }) => {
  const [form, setForm]       = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  React.useEffect(() => {
    if (game) {
      setForm({
        categorie:       game.categorie || '',
        round:           game.round || '',
        gameDate:        game.gameDate ? format(new Date(game.gameDate), 'yyyy-MM-dd') : '',
        startTime:       game.startTime || '',
        status:          game.status || 'scheduled',
        'venue.name':    game.venue?.name || '',
        'venue.address': game.venue?.address || '',
        'venue.city':    game.venue?.city || '',
        'score.entity1': game.score?.entity1 ?? 0,
        'score.entity2': game.score?.entity2 ?? 0,
        notes:           game.notes || '',
      });
      setError('');
    }
  }, [game]);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        categorie: form.categorie,
        round:     form.round,
        gameDate:  form.gameDate,
        startTime: form.startTime,
        status:    form.status,
        venue: {
          name:    form['venue.name'],
          address: form['venue.address'],
          city:    form['venue.city'],
        },
        score: {
          entity1: Number(form['score.entity1']),
          entity2: Number(form['score.entity2']),
        },
        notes: form.notes,
      };
      await apiCall((headers) =>
        axios.put(`${BASE_URL}/games/${game._id}`, payload, { headers })
      );
      onRefresh();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Échec de la mise à jour du match.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: SURFACE,
          borderRadius: '16px',
          border: `1px solid ${BORDER}`,
          boxShadow: '0 8px 32px rgba(30,107,69,0.10)',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: TEXT_PRI, pb: 1 }}>
        Modifier le match
      </DialogTitle>
      <Divider sx={{ borderColor: BORDER }} />

      <DialogContent sx={{ pt: 2, bgcolor: BG }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2}>
          {/* Catégorie & Tour */}
          <Grid item xs={6}>
            <TextField
              label="Catégorie"
              name="categorie"
              select
              fullWidth
              size="small"
              value={form.categorie || ''}
              onChange={handleChange}
              sx={inputSx}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c} sx={{ color: TEXT_PRI }}>
                  {CATEGORY_LABELS[c] || c.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Tour / Journée"
              name="round"
              fullWidth
              size="small"
              value={form.round || ''}
              onChange={handleChange}
              sx={inputSx}
            />
          </Grid>

          {/* Date & Heure */}
          <Grid item xs={6}>
            <TextField
              label="Date du match"
              name="gameDate"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.gameDate || ''}
              onChange={handleChange}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Heure de début"
              name="startTime"
              fullWidth
              size="small"
              placeholder="ex. 15:00"
              value={form.startTime || ''}
              onChange={handleChange}
              sx={inputSx}
            />
          </Grid>

          {/* Scores */}
          <Grid item xs={6}>
            <TextField
              label={`Score — ${game?.entity1?.name || 'Équipe 1'}`}
              name="score.entity1"
              type="number"
              fullWidth
              size="small"
              inputProps={{ min: 0 }}
              value={form['score.entity1'] ?? 0}
              onChange={handleChange}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={`Score — ${game?.entity2?.name || 'Équipe 2'}`}
              name="score.entity2"
              type="number"
              fullWidth
              size="small"
              inputProps={{ min: 0 }}
              value={form['score.entity2'] ?? 0}
              onChange={handleChange}
              sx={inputSx}
            />
          </Grid>

          {/* Statut */}
          <Grid item xs={12}>
            <TextField
              label="Statut"
              name="status"
              select
              fullWidth
              size="small"
              value={form.status || ''}
              onChange={handleChange}
              sx={inputSx}
            >
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s} sx={{ color: TEXT_PRI }}>
                  {STATUS_LABELS[s] || s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Lieu */}
          <Grid item xs={12}>
            <Typography
              variant="caption"
              sx={{ color: TEXT_SEC, mb: 0.5, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.68rem' }}
            >
              Lieu
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Nom du stade / lieu"
              name="venue.name"
              fullWidth
              size="small"
              value={form['venue.name'] || ''}
              onChange={handleChange}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Adresse"
              name="venue.address"
              fullWidth
              size="small"
              value={form['venue.address'] || ''}
              onChange={handleChange}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Ville"
              name="venue.city"
              fullWidth
              size="small"
              value={form['venue.city'] || ''}
              onChange={handleChange}
              sx={inputSx}
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              label="Notes"
              name="notes"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={form.notes || ''}
              onChange={handleChange}
              sx={inputSx}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider sx={{ borderColor: BORDER }} />
      <DialogActions sx={{ px: 3, py: 2, bgcolor: SURFACE }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ color: TEXT_SEC, borderRadius: '10px', textTransform: 'none' }}
        >
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{
            bgcolor: ACCENT,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: `0 4px 14px rgba(30,107,69,0.30)`,
            '&:hover': { bgcolor: '#175c39', boxShadow: `0 6px 18px rgba(30,107,69,0.40)` },
          }}
        >
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Delete Dialog (multi-step) ───────────────────────────────────────────────
const DELETE_STEPS = ['Confirmer', 'Vérifier', 'Supprimer'];

const GameDeleteDialog = ({ open, onClose, game, onRefresh }) => {
  const [step, setStep]       = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  React.useEffect(() => {
    if (open) { setStep(0); setError(''); }
  }, [open]);

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      await apiCall((headers) =>
        axios.delete(`${BASE_URL}/games/${game._id}`, { headers })
      );
      onRefresh();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Échec de la suppression du match.');
    } finally {
      setLoading(false);
    }
  };

  const stepContent = [
    // Step 0 — Confirmer
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Warning color="warning" sx={{ fontSize: 32 }} />
        <Typography variant="h6" fontWeight={700} color={TEXT_PRI}>Êtes-vous sûr ?</Typography>
      </Box>
      <Typography sx={{ color: TEXT_SEC }}>
        Vous êtes sur le point de supprimer le match :
      </Typography>
      <Typography fontWeight="bold" sx={{ mt: 1, color: TEXT_PRI }}>
        {game?.entity1?.name} vs {game?.entity2?.name}
      </Typography>
      <Typography variant="body2" sx={{ color: TEXT_SEC }}>
        {game?.gameDate ? format(new Date(game.gameDate), 'dd MMM yyyy') : ''} — {game?.venue?.name}
      </Typography>
    </>,
    // Step 1 — Vérifier
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Warning color="error" sx={{ fontSize: 32 }} />
        <Typography variant="h6" fontWeight={700} color={TEXT_PRI}>Cette action est irréversible</Typography>
      </Box>
      <Alert severity="warning" sx={{ mb: 1, borderRadius: '10px' }}>
        Toutes les données du match (scores, buts, statistiques) seront définitivement supprimées.
      </Alert>
      <Typography variant="body2" sx={{ color: TEXT_SEC }}>
        Cliquez sur <b>Continuer</b> uniquement si vous comprenez que cette action ne peut pas être annulée.
      </Typography>
    </>,
    // Step 2 — Confirmation finale
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Warning color="error" sx={{ fontSize: 32 }} />
        <Typography variant="h6" fontWeight={700} color={TEXT_PRI}>Confirmation finale</Typography>
      </Box>
      <Typography sx={{ color: TEXT_SEC, mb: 1 }}>
        Cliquez sur <b>Supprimer définitivement</b> pour confirmer la suppression.
      </Typography>
      {error && <Alert severity="error" sx={{ mt: 1, borderRadius: '10px' }}>{error}</Alert>}
    </>,
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: SURFACE,
          borderRadius: '16px',
          border: `1px solid ${BORDER}`,
          boxShadow: '0 8px 32px rgba(30,107,69,0.10)',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: TEXT_PRI, pb: 1 }}>
        Supprimer le match
      </DialogTitle>
      <Divider sx={{ borderColor: BORDER }} />

      <DialogContent sx={{ pt: 2, bgcolor: BG }}>
        <Stepper
          activeStep={step}
          sx={{
            mb: 3,
            '& .MuiStepLabel-label': { color: TEXT_SEC, fontSize: '0.78rem' },
            '& .MuiStepLabel-label.Mui-active': { color: ACCENT, fontWeight: 700 },
            '& .MuiStepIcon-root.Mui-active': { color: ACCENT },
            '& .MuiStepIcon-root.Mui-completed': { color: ACCENT },
          }}
        >
          {DELETE_STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
        {stepContent[step]}
      </DialogContent>

      <Divider sx={{ borderColor: BORDER }} />
      <DialogActions sx={{ px: 3, py: 2, bgcolor: SURFACE }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ color: TEXT_SEC, borderRadius: '10px', textTransform: 'none' }}
        >
          Annuler
        </Button>
        {step < 2 ? (
          <Button
            variant="contained"
            color="warning"
            onClick={() => setStep((s) => s + 1)}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
          >
            Continuer
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
          >
            Supprimer définitivement
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ─── Chip styles for status & category ───────────────────────────────────────
const categoryChipSx = {
  bgcolor: ACCENT_BG,
  color: ACCENT,
  border: `1px solid ${BORDER}`,
  fontWeight: 600,
  fontSize: '0.68rem',
};

// ─── Main GamesTab ────────────────────────────────────────────────────────────
const GamesTab = ({ games = [], onRefresh, currentUserId, event }) => {
  const [selectedGame, setSelectedGame] = useState(null);
  const [editGame, setEditGame]         = useState(null);
  const [deleteGame, setDeleteGame]     = useState(null);

  const isSuperAdmin =
    !!currentUserId &&
    (event?.eventOwnersAndRoles || []).some((entry) => {
      const entryUserId = typeof entry.userId === 'object' ? entry.userId._id : entry.userId;
      return entryUserId === currentUserId && (entry.roles || []).includes('eventSuperAdmin');
    });

  if (!games.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center', bgcolor: BG, borderRadius: 2 }}>
        <SportsScore sx={{ fontSize: 48, color: BORDER, mb: 1 }} />
        <Typography sx={{ color: TEXT_SEC }}>Aucun match pour le moment</Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${BORDER}`,
          bgcolor: SURFACE,
          overflow: 'hidden',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: SURFACE }}>
              {['Match', 'Date', 'Lieu', 'Score', 'Statut', 'Actions'].map((label, i) => (
                <TableCell
                  key={label}
                  align={label === 'Actions' ? 'center' : 'left'}
                  sx={{
                    color: TEXT_SEC,
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    borderBottom: `1px solid ${BORDER}`,
                    py: 1.5,
                    bgcolor: BG,
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {games.map((g) => (
              <TableRow
                key={g._id}
                hover
                sx={{
                  '&:hover': { bgcolor: ACCENT_BG },
                  '& td': { borderColor: BORDER },
                }}
              >
                {/* Match */}
                <TableCell>
                  <Typography variant="body2" fontWeight="600" sx={{ color: TEXT_PRI }}>
                    {g.entity1?.name}{' '}
                    <span style={{ color: TEXT_SEC, fontWeight: 400 }}>vs</span>{' '}
                    {g.entity2?.name}
                  </Typography>
                  <Chip
                    label={CATEGORY_LABELS[g.categorie] || g.categorie?.replace(/_/g, ' ')}
                    size="small"
                    variant="outlined"
                    sx={{ mt: 0.4, ...categoryChipSx }}
                  />
                </TableCell>

                {/* Date */}
                <TableCell>
                  <Typography variant="body2" sx={{ color: TEXT_PRI }}>
                    {g.gameDate ? format(new Date(g.gameDate), 'dd MMM yyyy') : '-'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: TEXT_SEC }}>{g.startTime}</Typography>
                </TableCell>

                {/* Lieu */}
                <TableCell>
                  <Typography variant="body2" sx={{ color: TEXT_PRI }}>{g.venue?.name}</Typography>
                  <Typography variant="caption" sx={{ color: TEXT_SEC }}>{g.venue?.city}</Typography>
                </TableCell>

                {/* Score */}
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{
                      color: ACCENT,
                      bgcolor: ACCENT_BG,
                      px: 1,
                      py: 0.25,
                      borderRadius: '6px',
                      display: 'inline-block',
                      fontSize: '0.8rem',
                    }}
                  >
                    {g.score?.entity1} – {g.score?.entity2}
                  </Typography>
                </TableCell>

                {/* Statut */}
                <TableCell>
                  <Chip
                    label={STATUS_LABELS[g.status] || g.status}
                    color={statusColor[g.status] || 'default'}
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.72rem' }}
                  />
                </TableCell>

                {/* Actions */}
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => setSelectedGame(g)}
                    title="Voir"
                    sx={{
                      color: ACCENT,
                      bgcolor: ACCENT_BG,
                      borderRadius: '8px',
                      mr: 0.5,
                      '&:hover': { bgcolor: BORDER },
                    }}
                  >
                    <Visibility fontSize="small" />
                  </IconButton>

                  {isSuperAdmin && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => setEditGame(g)}
                        title="Modifier"
                        sx={{
                          color: '#1565c0',
                          bgcolor: '#e3f0ff',
                          borderRadius: '8px',
                          mr: 0.5,
                          '&:hover': { bgcolor: '#c8dcf8' },
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteGame(g)}
                        title="Supprimer"
                        sx={{
                          color: '#c62828',
                          bgcolor: '#fdecea',
                          borderRadius: '8px',
                          '&:hover': { bgcolor: '#f9cbc8' },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <GameViewDialog
        open={!!selectedGame}
        onClose={() => setSelectedGame(null)}
        game={selectedGame}
        onRefresh={onRefresh}
      />

      <GameEditDialog
        open={!!editGame}
        onClose={() => setEditGame(null)}
        game={editGame}
        onRefresh={onRefresh}
      />

      <GameDeleteDialog
        open={!!deleteGame}
        onClose={() => setDeleteGame(null)}
        game={deleteGame}
        onRefresh={onRefresh}
      />
    </>
  );
};

export default GamesTab;